import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MediaManager } from '@dev/ee-modules';
import { FileInfo } from '@dev/ee-api/ee-api-models';
import { Grid } from '@material-ui/core';
import styled from 'styled-components';
import { palette } from '@dev/ee-components';
import { EditorMediaManagerProps, EditorMediaManagerTabIndex, EditorFile } from '@/store/templateEditor/types';

import { ApplicationState } from '@/store';
import { HideDialog, ShowDialog } from '@/store/dialog/actions';
import { getUploadStore, UploadFilesMedia as UploadFiles } from '@/store/upload/actions';
import { getAccountStore } from '@/store/account/actions';
import { DeleteFile, DeleteFilesList, getUserFilesStore, LoadUserFiles } from '@/store/userfiles/actions';
import {
    getMediaManagerStore,
    HideMediaManager,
    HideMediaManagerUploader,
    ShowFileUpload,
    ShowMediaManagerUploader,
} from '@/store/mediaManager/actions';
import { ShowToast } from '@/store/toast/actions';
import { getEditorFilesStore, UploadEditorFileSuccess } from '@/store/templateEditor/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { FileType } from '@/store/mediaManager/types';
import { ShowToastParams } from '@/store/toast/types';

const Overlay = styled.div`
    background: ${palette.snow[1]};
    height: max-content;

    .MuiContainer-root {
        padding-top: 60px;
    }
`;

export const EditorMediaManager = (props: EditorMediaManagerProps): JSX.Element => {
    const { showImageTabOnly, blockId, isLinkToFile } = props;

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const account = useSelector(getAccountStore);
    const browser = useSelector((state: ApplicationState) => state.browser);
    const upload = useSelector(getUploadStore);
    const userFiles = useSelector(getUserFilesStore);
    const mediaManager = useSelector(getMediaManagerStore);
    const { filesList } = useSelector(getEditorFilesStore);

    const uploadFiles = (file: File) => dispatch(UploadFiles(file));
    const loadUserFiles = () => dispatch(LoadUserFiles());
    const deleteFile = (name: string, imageDelete: boolean) => dispatch(DeleteFile(name, imageDelete));
    const deleteFilesList = (filesList: string[], imagesDelete: boolean) =>
        dispatch(DeleteFilesList(filesList, imagesDelete));
    const showDialog = (params: ShowDialogParams) => dispatch(ShowDialog(params));
    const hideDialog = () => dispatch(HideDialog());
    const hideMediaManager = () => dispatch(HideMediaManager());
    const showMediaManagerUploader = () => dispatch(ShowMediaManagerUploader());
    const hideMediaManagerUploader = () => dispatch(HideMediaManagerUploader());
    const showFileUpload = (file: FileType) => dispatch(ShowFileUpload(file));
    const showToast = (params: ShowToastParams) => dispatch(ShowToast(params));

    const onFileSelected = (file: FileInfo) => {
        const fileUrl = `${APIPATH}/userfile/${account?.GeneralInfo?.PublicAccountID}/${file.FileName}`;

        const selectedFile: EditorFile = {
            fileName: file.FileName,
            fileUrl,
            blockId,
            isLinkToFile,
        };

        dispatch(UploadEditorFileSuccess([...filesList, selectedFile]));
        dispatch(HideMediaManager());
    };

    const defaultActiveTabIndex =
        !showImageTabOnly && isLinkToFile ? EditorMediaManagerTabIndex.FILE : EditorMediaManagerTabIndex.IMAGE;

    return (
        <Overlay>
            <Grid container justifyContent="center">
                <Grid item xs={12} sm={12}>
                    <MediaManager
                        onFileSelect={onFileSelected}
                        account={account}
                        browser={browser}
                        upload={upload}
                        userFiles={userFiles}
                        mediaManager={mediaManager}
                        APIPATH={APIPATH}
                        UploadFiles={uploadFiles}
                        LoadUserFiles={loadUserFiles}
                        DeleteFile={deleteFile}
                        DeleteFilesList={deleteFilesList}
                        ShowDialog={showDialog}
                        HideDialog={hideDialog}
                        HideMediaManager={hideMediaManager}
                        ShowMediaManagerUploader={showMediaManagerUploader}
                        HideMediaManagerUploader={hideMediaManagerUploader}
                        ShowFileUpload={showFileUpload}
                        ShowToast={showToast}
                        t={t}
                        imageTabOnly={showImageTabOnly}
                        defaultActiveTabIndex={defaultActiveTabIndex}
                    />
                </Grid>
            </Grid>
        </Overlay>
    );
};
